import React, { useState, useRef, useEffect, useContext } from "react";
import FormikInput from "@acko-tech/building-blocks.ui.form";
import { validateFunction } from "@utils/validate";
import { styled } from "@acko-tech/building-blocks.ui.common";
import { Text } from "@acko-tech/building-blocks.ui.typography";
import InputField from "@components/inputField";
import { InputFieldStyles, Heading } from "./styles";
import { getErrorMessageForId, getWidgetSpecificProps } from "@utils/index";
import { connect, useDispatch, useSelector } from "react-redux";
// import { setPayload } from "@actions/setPayloadData";
// import { debounce } from "utils/lodash";
import {
  resetFieldErrorData,
  setData,
  setPayloadData,
  setFieldErrorData
} from "@store/slices/globalState";
import { Context } from "@components/useContext/context";
import { setAnalytics } from "@actions/setAnalyticsData";
import { useOnload } from "@utils/useOnLoad";
import { scrollIntoViewWrapper } from "@utils/scrollToView";
import { setInSessionStorage } from "@actions/setInSessionStorage";
import { removeError } from "@actions/fetchConfigRedirect";
import { calculateCharacterDifference } from "@utils/validateOwnerName";
import { closeMobileKeyboard } from "@actions/closeMobileKeyboard";

const mapStateToProps = function (state: any, ownProps: any) {
  return {
    ...getWidgetSpecificProps(
      state.configuration?.normalisedConfig,
      ownProps.id
    ),
    errorMessage: getErrorMessageForId(
      state.configuration.normalisedConfig,
      ownProps.id
    )
  };
};

const Note = styled(Text)`
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #5b5675;
  margin-top: ${(props) => props.mt};
  @media (max-width: 768px) {
    width: 342px;
  }
`;

const InputFieldWrapper = (props: any) => {
  const dispatch = useDispatch();
  const {
    id,
    name,
    placeholder,
    defaultValue,
    type,
    cueText,
    is_visible,
    errorMessage,
    validate,
    payloadData,
    heading,
    styles,
    wrapperStyles,
    inputFieldStyles,
    actions,
    children,
    icon = false,
    iconStyles,
    mWrapperStyles,
    hideLabelText,
    shouldReadOnly,
    maskedValue,
    isDisabled = false,
    closeKeyboardOnEnter = false
  } = props;
  const { isDesktop } = useContext(Context);
  const [errors, setErrors] = useState({});
  const [showError, setShowError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { globalPayload, fieldErrorData } = useSelector(
    ({ globalState }: any) => {
      return {
        globalPayload: globalState?.payloadData,
        fieldErrorData: globalState?.fieldErrorData
      };
    }
  );

  const { on_load, exception_trigger } = actions || {};

  useOnload({ on_load, is_visible });

  const getPrefilledData = () => {
    if (payloadData?.value === 0) {
      return payloadData?.value;
    } else if (globalPayload[name]) {
      return globalPayload[name];
    }
    return payloadData?.value || "";
  };

  const [currentValue, setCurrentValue] = useState(getPrefilledData());
  const [isMaskedValueCleared, setIsMaskedValueCleared] = useState(false);
  const [displayValue, setDisplayValue] = useState(
    maskedValue || getPrefilledData()
  );
  // to keep track of previous value
  const [prevValue, setPrevValue] = useState(getPrefilledData());
  const originalNameRef = useRef("");

  useEffect(() => {
    if (id === "edit_owner_name_node_name_preferred_name") {
      // Get the current name from global state or props
      const currentName = globalPayload["vehicle_owner_name_current"] || ""; // or wherever you store it
      if (currentName && originalNameRef.current !== currentName) {
        originalNameRef.current = currentName;
      }
    }
  }, [id, globalPayload]);

  useEffect(() => {
    if (
      id === "review_edit_node_pincode" ||
      id === "review_edit_node_phone_number"
    ) {
      setInSessionStorage({ key: id, value: prevValue });
    }
  }, [id, prevValue]);

  useEffect(() => {
    if (payloadData?.value || payloadData?.value === 0) {
      setAnalytics({ [payloadData?.name]: payloadData?.value });
      dispatch(
        setPayloadData({
          [payloadData?.name]: payloadData?.value
        })
      );

      setCurrentValue(getPrefilledData());
    }
  }, [payloadData?.value]);

  useEffect(() => {
    if (currentValue || currentValue === 0) {
      setAnalytics({ [payloadData?.name]: currentValue });
      dispatch(
        setPayloadData({
          [payloadData?.name]: currentValue
        })
      );
    }
  }, [currentValue]);

  // Update: Only sync from Redux if not editing
  useEffect(() => {
    if (!isEditing && globalPayload && name) {
      const newValue = globalPayload[name];
      if (newValue !== undefined && newValue !== currentValue) {
        setCurrentValue(newValue);
        setDisplayValue(newValue);
      }
    }
  }, [globalPayload, name, isEditing]);

  const triggerExceptionEvent = () => {
    if (
      exception_trigger?.[0] &&
      typeof exception_trigger[0].funCall === "function"
    ) {
      exception_trigger?.forEach((eachFunc: any) => {
        eachFunc.funCall(eachFunc.args);
      });
    }
  };

  const handleBlur = () => {
    let field_error = false;

    if (id === "edit_owner_name_node_name_preferred_name") {
      const diffCount = calculateCharacterDifference(
        originalNameRef.current,
        currentValue
      );

      if (diffCount > 3) {
        const newErrors = {
          [name]: "You can edit only up to 3 characters"
        };
        field_error = true;
        setErrors(newErrors);
        setShowError(true);
        triggerExceptionEvent();
        dispatch(
          setFieldErrorData({
            message: "You can edit only up to 3 characters",
            componentName: name,
            show: true
          })
        );
        dispatch(setData({ field_error }));
        return;
      }
    }

    const isError = validateFunction({ currentValue, ...validate });
    if (isError) {
      const newErrors = {
        [name]: isError
      };
      field_error = true;
      setErrors(newErrors);
      setShowError(true);
      triggerExceptionEvent();
      dispatch(
        setFieldErrorData({
          message: isError,
          componentName: name,
          show: true
        })
      );
    } else {
      const newErrors = {
        [name]: ""
      };
      setErrors(newErrors);
      setShowError(false);
      removeError(id);
      dispatch(resetFieldErrorData());
    }
    dispatch(
      setData({
        field_error
      })
    );
  };

  useEffect(() => {
    if (errorMessage) {
      const newErrors = {
        [name]: validateFunction({ currentValue, ...validate }) || errorMessage
      };
      setErrors(newErrors);
      setShowError(true);
    } else if (fieldErrorData && fieldErrorData.componentName == name) {
      const newErrors = {
        [name]:
          validateFunction({ currentValue, ...validate }) ||
          fieldErrorData.message
      };
      setErrors(newErrors);
      setShowError(true);
    }
  }, [errorMessage, fieldErrorData]);

  const { on_click } = actions || {};

  const handleChange = () => {
    if (on_click?.[0] && typeof on_click[0].funCall === "function") {
      on_click?.forEach((eachFunc: any) => {
        eachFunc.funCall(eachFunc.args);
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (closeKeyboardOnEnter && (e.key === 'Enter' || e.keyCode === 13)) {
      closeMobileKeyboard({ event: e });
    }
  };

  return is_visible ? (
    <>
      <InputFieldStyles
        wrapperStyles={wrapperStyles}
        mWrapperStyles={mWrapperStyles}
        onClick={handleChange}
      >
        {heading && <Heading>{heading}</Heading>}
        <FormikInput
          placeholder={placeholder}
          name={name}
          initialValue={defaultValue || ""}
          validate={(values): any => {
            setErrors({});
            setShowError(false);
            dispatch(setPayloadData(values));
            dispatch(
              setData({
                field_error: false
              })
            );
            dispatch(resetFieldErrorData());
          }}
          buttonText=""
          autoFocus={false}
          handleNext={() => { }}
          buttonMargin="4em 0 0 0"
          textAlign={"left"}
          validateOnBlur
          enableReinitialize
          customInput={({ errors: formikErrors, ...props }: any) => (
            <InputField
              errors={{ ...errors, ...formikErrors }}
              {...props}
              placeholder={placeholder}
              name={name}
              type={type}
              value={displayValue}
              onChange={(value) => {
                setCurrentValue(value);
                setDisplayValue(value);
              }}
              handleBlur={() => {
                setIsEditing(false);
                handleBlur();
              }}
              onFocus={() => {
                setIsEditing(true);
                // TODO: fix this
                // this is a hack to prevent unwanted scrolling in APP IOS
                if (maskedValue && !isMaskedValueCleared) {
                  setIsMaskedValueCleared(true);
                  setCurrentValue("");
                  setDisplayValue("");
                  dispatch(
                    setPayloadData({
                      [payloadData?.name]: ""
                    })
                  );
                }
                setTimeout(() => {
                  scrollIntoViewWrapper(name);
                }, 30);
              }}
              onKeyDown={closeKeyboardOnEnter ? handleKeyDown : undefined}
              setShowError={() => { }}
              inputContainerStyles={{
                height: "50px",
                marginTop: "12px",
                marginBottom:
                  showError && errors && errors[name] ? "10px" : "0px",
                ...styles
              }}
              inputStyles={{
                padding: "12px 16px",
                height: "48px",
                "box-sizing": "border-box",
                ...inputFieldStyles
              }}
              className="fs-exclude"
              Icon={icon}
              iconStyles={iconStyles}
              showError={showError}
              hideLabelText={hideLabelText}
              readOnly={shouldReadOnly}
              disabled={isDisabled}
            />
          )}
        />
        {cueText && <Note mt={showError && errors && errors[name] ? "20px" : "4px"}>{cueText}</Note>}
      </InputFieldStyles>
      {children}
    </>
  ) : (
    <></>
  );
};

export default connect(mapStateToProps)(InputFieldWrapper);
